<template>
  <div v-if="isLoading" class="content">
    <LoadingSpinner />
  </div>
  <div v-else class="content max-700 m-auto">
    <h1>{{ tn("createAccount") }}</h1>
    <ul class="form mb-triple">
      <li>
        <label for="create-email">{{ t("Common.email") }}</label>
        <input id="create-email" v-model="userStore.email" :disabled="true" type="text" />
        <a @:click="goToGetStartedPage()">{{ tn("useDifferentEmail") }}</a>
      </li>
      <li>
        <div class="label-wrapper">
          <label for="create-password">{{ t("Common.password") }}</label>
          <button
            v-if="setAccountPasswordHidden"
            type="button"
            class="inline-btn-link"
            title="Show password"
            @click="setAccountPasswordHidden = !setAccountPasswordHidden"
          >
            {{ t("Common.show") }}
          </button>
          <button
            v-else
            type="button"
            class="inline-btn-link"
            title="Hide password"
            @click="setAccountPasswordHidden = !setAccountPasswordHidden"
          >
            {{ t("Common.hide") }}
          </button>
        </div>
        <InputErrorComponent :show="setPasswordErrors && setPasswordErrors.length > 0">
          <input
            id="create-password"
            v-model.trim="setAccountPassword"
            :type="setAccountPasswordHidden ? 'password' : 'text'"
            autocomplete="off"
            @keyup="dirtyPasswordFlag = true"
          />
          <template #error>
            <div>
              <span v-for="e in setPasswordErrors" :key="e.name">{{ e.message }}</span>
            </div>
          </template>
        </InputErrorComponent>
        <p class="mt-quarter">{{ tn("passwordRequirements") }}</p>
        <PasswordRulesComponent :password="setAccountPassword" :show-rules="false" />
      </li>
      <li>
        <div class="label-wrapper">
          <label for="confirm-password">{{ tn("confirmPassword") }}</label>
          <button
            v-if="setAccountConfirmPasswordHidden"
            type="button"
            class="inline-btn-link"
            title="Show Confirm password"
            @click="setAccountConfirmPasswordHidden = !setAccountConfirmPasswordHidden"
          >
            {{ t("Common.show") }}
          </button>
          <button
            v-else
            type="button"
            class="inline-btn-link"
            title="Hide password"
            @click="setAccountConfirmPasswordHidden = !setAccountConfirmPasswordHidden"
          >
            {{ t("Common.hide") }}
          </button>
        </div>
        <InputErrorComponent :show="showPasswordConfirmError">
          <input
            id="confirm-password"
            v-model.trim="setAccountConfirmPassword"
            :type="setAccountConfirmPasswordHidden ? 'password' : 'text'"
            autocomplete="off"
            @keyup="dirtyConfirmPasswordFlag = true"
          />
          <template #error>
            <div>
              <span>{{ tn("passwordNotMatch") }}</span>
            </div>
          </template>
        </InputErrorComponent>
      </li>
      <button
        id="btnCreateAccount"
        type="submit"
        class="btn-primary position-relative"
        :disabled="!setPasswordEnabled || settingPassword"
        @click="createAccount()"
      >
        <spinner :is-spinning="settingPassword" />
        {{ tn("createAccount") }}
      </button>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { validatePassword } from "@/common/strongPassword";
import { reportStringErrors } from "@/common/validator";
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from "@/define";
import { passwordRegex } from "@/globalRegex";
import { t } from "@/i18n";
import { useUserStore } from "@/stores/user";
import { computed, onBeforeMount, ref } from "vue";
import PasswordRulesComponent from "@/components/shared/PasswordRules.vue";
import InputErrorComponent from "@/components/shared/InputError.vue";
import { addPasswordAol } from "./AolCreateAccountHelper";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import Spinner from "@/components/shared/Spinner.vue";
import router from "@/routes";

const userStore = useUserStore();

const setAccountPasswordHidden = ref<boolean>(true);
const setAccountPassword = ref<string>("");
const dirtyPasswordFlag = ref<boolean>(false);
const setAccountConfirmPasswordHidden = ref<boolean>(true);
const setAccountConfirmPassword = ref<string>("");
const dirtyConfirmPasswordFlag = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const settingPassword = ref<boolean>(false);

onBeforeMount(async () => {
  isLoading.value = true;
  await reloadSubscriberInfo();
  userStore.email = userStore.currentUser?.email ?? "";
  isLoading.value = false;
});

const setPasswordErrors = computed(() => {
  const result = validatePassword(setAccountPassword.value);

  //Piggybacking reportStringErrors on top of validatePassword to return
  //error message that displays the invalid characters used.
  result.push({
    name: "invalid_characters",
    message: passwordInvalidCharacterError.value,
    displayOrder: 5,
    passed: passwordInvalidCharacterError.value.length === 0,
  });

  const isNotValidPassword = result.some(e => !e.passed) && dirtyPasswordFlag.value === true;
  return isNotValidPassword ? [result.filter(e => !e.passed)[0]] : [];
});

const passwordInvalidCharacterError = computed(() => {
  return reportStringErrors(
    setAccountPassword.value,
    t("Common.password"),
    MIN_PASSWORD_LENGTH,
    MAX_PASSWORD_LENGTH,
    passwordRegex
  );
});

const showPasswordConfirmError = computed(() => {
  return (
    !!setAccountPassword.value &&
    !!dirtyConfirmPasswordFlag.value &&
    setAccountPassword.value !== setAccountConfirmPassword.value
  );
});

const setPasswordEnabled = computed(() => {
  if (showPasswordConfirmError.value) return false;
  if (setPasswordErrors.value.length > 0) return false;

  return (
    !!userStore.email &&
    !!setAccountPassword.value &&
    !!setAccountConfirmPassword.value &&
    validatePassword(setAccountPassword.value)
  );
});

const componentName = "AddPasswordAol";

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

async function createAccount() {
  settingPassword.value = true;
  await addPasswordAol(userStore.currentUser?.userGuid, setAccountPassword.value, componentName);
  settingPassword.value = false;
}

function goToGetStartedPage() {
  router.back();
}
</script>
